import React, { useState } from 'react';
import {
    Plus, Folder, Mic, FileText, Target,
    Calendar, Clock, ChevronRight, ChevronDown, Pencil, Trash2
} from 'lucide-react';

const countForProject = (list, projectId) =>
    (list || []).filter((x) => x && x.projectId === projectId).length;

const BrainTab = ({
    projects,
    recordings,
    documents,
    tasks,
    setShowCreateProjectModal,
    onAssignItemProject,
    onRenameProject,
    deleteProject
}) => {
    const [openProjectId, setOpenProjectId] = useState(null);
    const [editingProjectId, setEditingProjectId] = useState(null);
    const [editNameDraft, setEditNameDraft] = useState('');
    const taskCompletionPct =
        tasks.length > 0
            ? Math.round((tasks.filter((t) => t.completed).length / tasks.length) * 100)
            : 0;

    const getRecentActivity = () => {
        const activities = [];

        // Add recent recordings
        recordings.slice(0, 3).forEach(recording => {
            activities.push({
                id: `recording-${recording.id}`,
                type: 'recording',
                title: `${recording.category} Recording`,
                subtitle: `${Math.floor(recording.duration / 60)}:${(recording.duration % 60).toString().padStart(2, '0')} duration`,
                timestamp: recording.timestamp,
                icon: Mic,
                color: 'text-blue-600 bg-blue-100'
            });
        });

        // Add recent documents
        documents.slice(0, 2).forEach(doc => {
            activities.push({
                id: `document-${doc.id}`,
                type: 'document',
                title: doc.name,
                subtitle: `${(doc.size / 1024).toFixed(1)} KB`,
                timestamp: doc.uploadDate,
                icon: FileText,
                color: 'text-purple-600 bg-purple-100'
            });
        });

        // Add recent tasks
        tasks.filter(t => !t.completed).slice(0, 2).forEach(task => {
            activities.push({
                id: `task-${task.id}`,
                type: 'task',
                title: task.text,
                subtitle: `${task.priority} priority`,
                timestamp: task.createdAt,
                icon: Target,
                color: 'text-orange-600 bg-orange-100'
            });
        });

        // Sort by timestamp (most recent first)
        return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 6);
    };

    const recentActivity = getRecentActivity();

    return (
        <div className="space-y-8">
            {/* Knowledge Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                            <Mic className="w-8 h-8 text-blue-100" />
                            <span className="text-blue-100 text-sm font-medium">Total</span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-3xl font-bold">{recordings.length}</p>
                            <p className="text-blue-100 text-sm">Audio Recordings</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                            <FileText className="w-8 h-8 text-purple-100" />
                            <span className="text-purple-100 text-sm font-medium">Library</span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-3xl font-bold">{documents.length}</p>
                            <p className="text-purple-100 text-sm">Documents</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                            <Target className="w-8 h-8 text-green-100" />
                            <span className="text-green-100 text-sm font-medium">{taskCompletionPct}%</span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-3xl font-bold">{tasks.length}</p>
                            <p className="text-green-100 text-sm">Smart Tasks</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6"></div>
                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                            <Folder className="w-8 h-8 text-orange-100" />
                            <span className="text-orange-100 text-sm font-medium">Active</span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-3xl font-bold">{projects.length}</p>
                            <p className="text-orange-100 text-sm">Projects</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Projects Section */}
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">Projects</h3>
                        <p className="text-gray-600 text-sm">Organize your knowledge by projects</p>
                    </div>
                    <button
                        onClick={() => setShowCreateProjectModal(true)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Project
                    </button>
                </div>

                {projects.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {projects.map((project) => {
                            const docsIn = documents.filter((d) => d.projectId === project.id);
                            const recsIn = recordings.filter((r) => r.projectId === project.id);
                            const tasksIn = tasks.filter((t) => t.projectId === project.id);
                            const isOpen = openProjectId === project.id;
                            return (
                                <div
                                    key={project.id}
                                    className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-all duration-200"
                                >
                                    <div className="p-4">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div className="flex items-start gap-2 min-w-0 flex-1">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setOpenProjectId((prev) => (prev === project.id ? null : project.id))
                                                    }
                                                    className="mt-0.5 text-gray-500 flex-shrink-0 p-0.5 rounded hover:bg-gray-200/60"
                                                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                                                >
                                                    {isOpen ? (
                                                        <ChevronDown className="w-4 h-4" />
                                                    ) : (
                                                        <ChevronRight className="w-4 h-4" />
                                                    )}
                                                </button>
                                                {editingProjectId === project.id ? (
                                                    <input
                                                        className="font-semibold text-gray-900 w-full min-w-0 border border-blue-300 rounded px-2 py-0.5 text-sm"
                                                        value={editNameDraft}
                                                        autoFocus
                                                        onChange={(e) => setEditNameDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                if (onRenameProject && editNameDraft.trim()) {
                                                                    onRenameProject(project.id, editNameDraft);
                                                                }
                                                                setEditingProjectId(null);
                                                            }
                                                            if (e.key === 'Escape') {
                                                                setEditingProjectId(null);
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (onRenameProject && editNameDraft.trim() && editNameDraft.trim() !== project.name) {
                                                                onRenameProject(project.id, editNameDraft);
                                                            }
                                                            setEditingProjectId(null);
                                                        }}
                                                    />
                                                ) : (
                                                    <h4
                                                        className="font-semibold text-gray-900 break-words cursor-pointer"
                                                        onClick={() =>
                                                            setOpenProjectId((prev) => (prev === project.id ? null : project.id))
                                                        }
                                                    >
                                                        {project.name}
                                                    </h4>
                                                )}
                                            </div>
                                            <div
                                                className="flex items-center gap-0.5 flex-shrink-0"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {onRenameProject && (
                                                    <button
                                                        type="button"
                                                        className="p-1.5 rounded-lg text-gray-500 hover:bg-white/80 hover:text-gray-800"
                                                        title="Rename project"
                                                        onClick={() => {
                                                            setEditingProjectId(project.id);
                                                            setEditNameDraft(project.name || '');
                                                        }}
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {deleteProject && (
                                                    <button
                                                        type="button"
                                                        className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                                                        title="Delete project"
                                                        onClick={() => {
                                                            if (window.confirm(`Delete project “${project.name}”?`)) {
                                                                deleteProject(project.id);
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                <Folder className="w-5 h-5 text-blue-600 ml-0.5" />
                                            </div>
                                        </div>
                                        {project.description && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setOpenProjectId((prev) => (prev === project.id ? null : project.id))
                                                }
                                                className="w-full text-left text-sm text-gray-600 mb-3 line-clamp-2 pl-8"
                                            >
                                                {project.description}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenProjectId((prev) => (prev === project.id ? null : project.id))
                                            }
                                            className="w-full text-left pl-8"
                                        >
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                <div className="bg-white rounded-lg p-2">
                                                    <p className="text-sm font-bold text-blue-600">
                                                        {countForProject(recordings, project.id)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">Audio</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-2">
                                                    <p className="text-sm font-bold text-purple-600">
                                                        {countForProject(documents, project.id)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">Docs</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-2">
                                                    <p className="text-sm font-bold text-green-600">
                                                        {countForProject(tasks, project.id)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">Tasks</p>
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                    {isOpen && (
                                        <div className="px-4 pb-4 border-t border-gray-200/80 bg-white/50">
                                            <div className="pt-3 space-y-3 text-sm max-h-72 overflow-y-auto">
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                                        Documents
                                                    </p>
                                                    {docsIn.length ? (
                                                        <ul className="space-y-1 text-gray-800">
                                                            {docsIn.map((d) => (
                                                                <li key={d.id} className="truncate" title={d.name}>
                                                                    {d.name || d.id}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-gray-500 text-xs">No documents in this project</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                                        Recordings
                                                    </p>
                                                    {recsIn.length ? (
                                                        <ul className="space-y-1 text-gray-800">
                                                            {recsIn.map((r) => (
                                                                <li key={r.id} className="truncate" title={r.name || r.id}>
                                                                    {r.name ||
                                                                        (r.category
                                                                            ? `${r.category} recording`
                                                                            : 'Recording')}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-gray-500 text-xs">No recordings in this project</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                                        Tasks
                                                    </p>
                                                    {tasksIn.length ? (
                                                        <ul className="space-y-1 text-gray-800">
                                                            {tasksIn.map((t) => (
                                                                <li key={t.id} className="truncate" title={t.text}>
                                                                    {t.text || t.id}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-gray-500 text-xs">No tasks in this project</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Folder className="w-8 h-8 text-gray-400" />
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h4>
                        <p className="text-gray-600 mb-4">Create your first project to organize your content</p>
                        <button
                            onClick={() => setShowCreateProjectModal(true)}
                            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Create Project
                        </button>
                    </div>
                )}
            </div>

            {onAssignItemProject && projects.length > 0 && (
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Add content to a project (brain + RAG)</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Assigning a project updates Firestore and re-embeds so chat and search stay scoped. Unassigned
                        items only appear when you search across all projects.
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto text-sm">
                        {documents
                            .filter((d) => !d.projectId)
                            .slice(0, 12)
                            .map((d) => (
                                <div
                                    key={`u-doc-${d.id}`}
                                    className="flex flex-wrap items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2"
                                >
                                    <span className="text-gray-800 truncate max-w-xs">{d.name || d.id}</span>
                                    <select
                                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                onAssignItemProject('document', d.id, e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Assign to…</option>
                                        {projects.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        {recordings
                            .filter((r) => !r.projectId)
                            .slice(0, 8)
                            .map((r) => (
                                <div
                                    key={`u-rec-${r.id}`}
                                    className="flex flex-wrap items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2"
                                >
                                    <span className="text-gray-800 truncate max-w-xs">
                                        {r.name || (r.category ? `${r.category} recording` : 'Recording')}
                                    </span>
                                    <select
                                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                onAssignItemProject('recording', r.id, e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Assign to…</option>
                                        {projects.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        {tasks
                            .filter((t) => !t.projectId)
                            .slice(0, 8)
                            .map((t) => (
                                <div
                                    key={`u-task-${t.id}`}
                                    className="flex flex-wrap items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2"
                                >
                                    <span className="text-gray-800 truncate max-w-sm">{t.text || t.id}</span>
                                    <select
                                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
                                        value=""
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                onAssignItemProject('task', t.id, e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Assign to…</option>
                                        {projects.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                    </div>
                    {!documents.filter((d) => !d.projectId).length &&
                    !recordings.filter((r) => !r.projectId).length &&
                    !tasks.filter((t) => !t.projectId).length ? (
                        <p className="text-sm text-gray-500 mt-2">Everything is assigned to a project, or you have no items yet.</p>
                    ) : null}
                </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200/50 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">Recent Activity</h3>
                        <p className="text-gray-600 text-sm">Latest updates across your brain</p>
                    </div>
                    <Clock className="w-5 h-5 text-gray-400" />
                </div>

                {recentActivity.length > 0 ? (
                    <div className="space-y-3">
                        {recentActivity.map(activity => (
                            <div key={activity.id} className="flex items-center space-x-4 p-3 bg-gray-50/80 rounded-xl hover:bg-gray-100/80 transition-colors">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activity.color}`}>
                                    <activity.icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{activity.title}</p>
                                    <p className="text-sm text-gray-600">{activity.subtitle}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-500">
                                        {new Date(activity.timestamp).toLocaleDateString()}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {new Date(activity.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No recent activity</p>
                        <p className="text-sm text-gray-400">Start creating content to see activity here</p>
                    </div>
                )}
            </div>

        </div>
    );
};

export default BrainTab;